import React from 'react';
import texts from './lang/en.json';
import './App.css';

import { BackgroundDots } from './components/BackgroundDots';
import { CountdownControls } from './components/CountdownControls';
import { AnimatedNumber } from './components/AnimatedNumber';
import { WeatherWidget } from './components/WeatherWidget';

import { parseTargetDate } from './utils/date';
import { computeCountdownSnapshot } from './countdown/snapshot';
import { COUNTDOWN_MODES } from './countdown/modes';
import { TARGET_DATE_PRESETS, type CountdownModeId, type CountdownParts, type CountdownSnapshot, type TargetDatePresetId } from './countdown/types';
import type { WeatherState } from './types/weather';

const DEFAULT_BUQUEZ_TARGET_DATE = '2026-07-01';

const BACKGROUND_IMAGES = ['/img/IMG_9695.jpeg', '/img/IMG_3672.jpeg', '/img/IMG_1388.jpeg'];

const DRAGE_CROATIA = {
    latitude: 43.88944,
    longitude: 15.54083,
    label: 'Drage, HR',
} as const;

const App: React.FC = () => {
    const [backgroundIndex, setBackgroundIndex] = React.useState<number>(0);
    const [countdownMode, setCountdownMode] = React.useState<CountdownModeId>('standard');

    const [targetPresetId, setTargetPresetId] = React.useState<TargetDatePresetId>(() => {
        const env = (process.env.REACT_APP_BUQUEZ_TARGET_DATE || DEFAULT_BUQUEZ_TARGET_DATE).trim();
        const match = TARGET_DATE_PRESETS.find((preset) => preset.raw === env);
        return match?.id ?? TARGET_DATE_PRESETS[0].id;
    });

    const activeTargetPreset = React.useMemo(() => {
        return TARGET_DATE_PRESETS.find((preset) => preset.id === targetPresetId) ?? TARGET_DATE_PRESETS[0];
    }, [targetPresetId]);

    const targetDate = React.useMemo(() => {
        return parseTargetDate(activeTargetPreset.raw, DEFAULT_BUQUEZ_TARGET_DATE);
    }, [activeTargetPreset.raw]);

    const [snapshot, setSnapshot] = React.useState<CountdownSnapshot>(() => computeCountdownSnapshot(targetDate, new Date()));

    const [testMinutesOverride, setTestMinutesOverride] = React.useState<number | null>(null);
    const resetTestOverrideOnNextTickRef = React.useRef<boolean>(false);

    const [weather, setWeather] = React.useState<WeatherState | null>(null);

    React.useEffect(() => {
        const timer = window.setInterval(() => {
            setSnapshot(computeCountdownSnapshot(targetDate, new Date()));

            if (resetTestOverrideOnNextTickRef.current) {
                resetTestOverrideOnNextTickRef.current = false;
                setTestMinutesOverride(null);
            }
        }, 1000);
        return () => window.clearInterval(timer);
    }, [targetDate]);

    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            const isTextInput =
                target !== null &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.tagName === 'SELECT' ||
                    target.getAttribute('contenteditable') === 'true');
            if (isTextInput) return;

            // Hidden test shortcut: Ctrl + Alt + Shift + ArrowUp/ArrowDown
            if (event.ctrlKey && event.altKey && event.shiftKey) {
                const delta = event.key === 'ArrowUp' ? 1 : event.key === 'ArrowDown' ? -1 : 0;
                if (delta === 0) return;

                event.preventDefault();

                const baseMinutes = testMinutesOverride ?? snapshot.parts.minutes;
                const next = Math.max(0, Math.min(59, baseMinutes + delta));
                setTestMinutesOverride(next);
                resetTestOverrideOnNextTickRef.current = true;
                return;
            }

            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();

                const direction = event.key === 'ArrowRight' ? 1 : -1;
                setBackgroundIndex((current) => {
                    const next = current + direction;
                    return ((next % BACKGROUND_IMAGES.length) + BACKGROUND_IMAGES.length) % BACKGROUND_IMAGES.length;
                });
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [snapshot.parts.minutes, testMinutesOverride]);

    React.useEffect(() => {
        let cancelled = false;
        const abortController = new AbortController();

        const loadWeather = async () => {
            try {
                const latitude = DRAGE_CROATIA.latitude;
                const longitude = DRAGE_CROATIA.longitude;
                const locationLabel = DRAGE_CROATIA.label;

                const forecastUrl = `https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(
                    String(latitude)
                )}&longitude=${encodeURIComponent(String(longitude))}&current=temperature_2m,weather_code&timezone=auto`;
                const forecastRes = await fetch(forecastUrl, { signal: abortController.signal });
                if (!forecastRes.ok) throw new Error('Forecast failed');
                const forecastJson = (await forecastRes.json()) as {
                    current?: { temperature_2m?: number; weather_code?: number };
                    current_weather?: { temperature?: number; weathercode?: number };
                };

                const temperatureC = forecastJson.current?.temperature_2m ?? forecastJson.current_weather?.temperature ?? null;
                const weatherCode = forecastJson.current?.weather_code ?? forecastJson.current_weather?.weathercode ?? null;

                if (temperatureC === null || weatherCode === null) throw new Error('Missing weather data');
                if (cancelled) return;

                setWeather({
                    temperatureC: Math.round(temperatureC),
                    weatherCode,
                    locationLabel,
                });
            } catch {
                if (!cancelled) setWeather(null);
            }
        };

        loadWeather();
        const refresh = window.setInterval(loadWeather, 10 * 60 * 1000);

        return () => {
            cancelled = true;
            abortController.abort();
            window.clearInterval(refresh);
        };
    }, []);

    const setBackground = (index: number) => {
        const safeIndex = ((index % BACKGROUND_IMAGES.length) + BACKGROUND_IMAGES.length) % BACKGROUND_IMAGES.length;
        setBackgroundIndex(safeIndex);
    };

    const displayParts: CountdownParts = React.useMemo(() => {
        if (testMinutesOverride === null) return snapshot.parts;
        return { ...snapshot.parts, minutes: testMinutesOverride };
    }, [snapshot.parts, testMinutesOverride]);

    const tiles = React.useMemo(() => {
        const mode = COUNTDOWN_MODES.find((m) => m.id === countdownMode) || COUNTDOWN_MODES[0];
        const baseSnapshot = countdownMode === 'standard' ? { ...snapshot, parts: displayParts } : snapshot;
        return mode.getTiles(baseSnapshot);
    }, [countdownMode, snapshot, displayParts]);

    const activeModeLabel = React.useMemo(() => {
        return COUNTDOWN_MODES.find((m) => m.id === countdownMode)?.label ?? 'Standard';
    }, [countdownMode]);

    const cycleCountdownMode = () => {
        setCountdownMode((current) => {
            const index = COUNTDOWN_MODES.findIndex((m) => m.id === current);
            const nextIndex = index < 0 ? 0 : (index + 1) % COUNTDOWN_MODES.length;
            return COUNTDOWN_MODES[nextIndex].id;
        });
    };

    return (
        <div className="page" style={{ backgroundImage: `url(${BACKGROUND_IMAGES[backgroundIndex]})` }}>
            {weather && <WeatherWidget weather={weather} />}

            <div className="content">
                <BackgroundDots count={BACKGROUND_IMAGES.length} activeIndex={backgroundIndex} onSelect={setBackground} />

                <div className="overlay">
                    <CountdownControls
                        activeModeLabel={activeModeLabel}
                        onCycleMode={cycleCountdownMode}
                        activeTargetLabel={activeTargetPreset.label}
                        targetPresetId={targetPresetId}
                        onSelectTargetPreset={setTargetPresetId}
                    />

                    <div className="title">Countdown to Buqez</div>

                    <div className={`grid${tiles.length === 1 ? ' gridSingle' : ''}`}>
                        {tiles.map((tile) => (
                            <div key={tile.key} className="tile">
                                <div className="value"><AnimatedNumber value={tile.value} /></div>
                                <div className="label">{tile.label}</div>
                            </div>
                        ))}
                    </div>

                    <div className="footer">{texts.footerContact}</div>
                </div>
            </div>
        </div>
    );
};

export default App;
